// T-11.4: hub-and-spoke internal linking.
//
// Maps each LIVE service page to the blog posts most relevant to it (the
// service's "cluster"), by matching slug/title keywords -- the same
// mechanism src/lib/blogCategories.ts already uses for the blog's own
// category hubs, applied here to services instead of topics.
//
// A post CAN match more than one service's keyword list (e.g. a post about
// Stripe rebilling is relevant to both saas-crm and white-label-support);
// `postsForService` returns every match, and `primaryServiceForPost` (used
// for the blog post's own back-link) picks the single best one by matching
// against SERVICE_CLUSTERS in the order they're declared below, most
// specific first.
//
// Do not add a cluster whose service page doesn't exist or is currently
// redirected -- check liveServiceSlugs() in src/lib/llms.ts before adding one.

export type ServiceCluster = {
  /** Matches a FACTS.services[].url / the route under /services/<slug>. */
  serviceSlug: string
  serviceUrl: string
  serviceName: string
  /** Lower-case slug/title tokens (or hyphenated phrases) that place a post in this cluster. */
  keywords: string[]
}

export const SERVICE_CLUSTERS: ServiceCluster[] = [
  {
    serviceSlug: 'a2p-compliance',
    serviceUrl: '/services/a2p-compliance',
    serviceName: 'A2P Compliance',
    keywords: ['a2p', '10dlc', 'leadconnector-sms', 'sms-compliance', 'carrier-registration', 'sms', 'twilio', 'consent', 'campaign', 'texting', 'opt-out', 'two-way-sms', 'sms-templates', 'leadconnector', 'text-message', 'phone-system', 'sms-marketing', 'compliance', 'messaging'],
  },
  {
    serviceSlug: 'voice-ai',
    serviceUrl: '/services/voice-ai',
    serviceName: 'Voice AI',
    keywords: ['voice-ai', 'voice-agent', 'voice-cloning', 'receptionist', 'call-transfer', 'ai-call', 'voice', 'retell', 'vapi', 'missed-call'],
  },
  {
    serviceSlug: 'api-integrations',
    serviceUrl: '/services/api-integrations',
    serviceName: 'API Integrations',
    keywords: ['api-integrations', 'webhooks', 'zapier', 'n8n', 'make-vs', 'integrations', 'connect-gohighlevel'],
  },
  {
    serviceSlug: 'ghl-snapshots',
    serviceUrl: '/services/ghl-snapshots',
    serviceName: 'GHL Snapshots',
    keywords: ['snapshot', 'snapshots', 'snapshot-template', 'sub-account-template', 'template', 'sub-account', 'onboarding', 'deploy', 'workflow'],
  },
  {
    serviceSlug: 'ai-agent-builder',
    serviceUrl: '/services/ai-agent-builder',
    serviceName: 'AI Agent Builder',
    keywords: [
      'voice-ai', 'voice-cloning', 'simpletalk', 'retell', 'receptionist', 'chat-ai', 'ai-employee',
      'ai-agent', 'ai-reminders', 'chatbot', 'ai-studio', 'call-transfer',
    ],
  },
  {
    serviceSlug: 'vibe-coding',
    serviceUrl: '/services/vibe-coding',
    serviceName: 'Vibe Coding & Custom Development',
    keywords: [
      'vibe-coding', 'custom-development', 'custom-objects', 'api-integrations', 'custom-code', 'javascript-api',
      'zapier', 'n8n', 'make-vs', 'webhooks', 'niche-gohighlevel-integrations', 'connect-gohighlevel-to-claude',
      'best-gohighlevel-integrations',
    ],
  },
  {
    serviceSlug: 'saas-crm',
    serviceUrl: '/services/saas-crm',
    serviceName: 'SaaS CRM Launch',
    keywords: ['saas-mode', 'rebilling', 'stripe', 'reselling', 'resell', 'software-company'],
  },
  {
    serviceSlug: 'white-label-support',
    serviceUrl: '/services/white-label-support',
    serviceName: 'White-Label Support',
    keywords: ['white-label', 'trust-ghl-prime-white-label-partner'],
  },
  {
    serviceSlug: 'ghl-experts-for-agencies',
    serviceUrl: '/services/ghl-experts-for-agencies',
    serviceName: 'GHL Experts for Agencies',
    keywords: [
      'cost-to-hire', 'hire-gohighlevel', 'freelancer', 'agencies', 'agency', 'hubspot', 'franchises',
      'outgrown-spreadsheets', 'how-can-i-get-my-gohighlevel-for-free',
    ],
  },
  {
    serviceSlug: 'cost-effective-ghl-team',
    serviceUrl: '/services/cost-effective-ghl-team',
    serviceName: 'Cost-Effective GHL Team',
    keywords: ['cost-to-hire', 'pricing-2026', 'ai-employee-pricing'],
  },
  {
    serviceSlug: 'automation',
    serviceUrl: '/services/automation',
    serviceName: 'Workflow Automation',
    keywords: [
      'automation', 'automations', 'workflow', 'workflows', 'drip', 'follow-up', 'speed-to-lead', 'nurturing',
      'reminder', 'review', 're-engage', 'birthday', 'text-back', 'wait-steps', 'if-else', 'mistakes',
      'missed-call', 'pipeline-automation', 'onboarding-workflow', 'document-signing', 'email', 'bulk-email',
      'lead-source-tracking', 'lead-scoring', 'lead-management', 'roi', 'reporting-dashboards',
    ],
  },
  {
    serviceSlug: 'ghl-setup',
    serviceUrl: '/services/ghl-setup',
    serviceName: 'GHL Setup & Configuration',
    keywords: [
      'setup', 'set-up', 'sub-account', 'dashboard-walkthrough', 'custom-fields', 'import-contacts', 'permissions',
      'settings-explained', 'snapshot', 'snapshots', 'pipeline', 'funnel', 'calendar-setup', 'migrate', 'backup',
      'tags', 'smart-lists', 'duplicate-contacts', 'multiple-locations', 'crm-setup', 'from-scratch', 'what-is-ghl',
      'beginner-guide', 'profile', 'a2p', '10dlc', 'leadconnector-explained', 'twilio-sms-not-delivering',
      'state-specific-compliance', 'merge-fields',
    ],
  },
]

type PostLike = { slug: string; title?: string }

function haystack(post: PostLike): { tokens: Set<string>; joined: string } {
  const joined = `${post.slug} ${post.title ?? ''}`.toLowerCase().replace(/[^a-z0-9]+/g, '-')
  const tokens = new Set(joined.split('-').filter(Boolean))
  return { tokens, joined: `-${joined}-` }
}

function matches(cluster: ServiceCluster, post: PostLike): boolean {
  const { tokens, joined } = haystack(post)
  return cluster.keywords.some((keyword) =>
    keyword.includes('-') ? joined.includes(`-${keyword}-`) : tokens.has(keyword),
  )
}

/** Every cluster a post matches, in SERVICE_CLUSTERS order. */
export function clustersForPost(post: PostLike): ServiceCluster[] {
  return SERVICE_CLUSTERS.filter((cluster) => matches(cluster, post))
}

/** The single cluster a post's own back-link should point to: its first
 *  (most specific) match, or null if it matches none. */
export function primaryClusterForPost(post: PostLike): ServiceCluster | null {
  return clustersForPost(post)[0] || null
}

/** Posts in a given service's cluster, most recent first, capped at `limit`. */
export function postsForService<T extends PostLike & { published_at?: string }>(
  serviceSlug: string,
  posts: T[],
  limit = 10,
): T[] {
  const cluster = SERVICE_CLUSTERS.find((c) => c.serviceSlug === serviceSlug)
  if (!cluster) return []
  return posts
    .filter((post) => matches(cluster, post))
    .sort((a, b) => (b.published_at || '').localeCompare(a.published_at || ''))
    .slice(0, limit)
}
